import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc,
  getDoc,      // ← AGREGADO
  addDoc,
  deleteDoc,
  updateDoc,   // ← AGREGADO
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Event, CreateEventData, UpdateEventData, EventStatus } from '../types/event.types';

/**
 * Obtener eventos de un usuario
 */
export const getUserEvents = async (userId: string): Promise<Event[]> => {
  try {
    const eventsRef = collection(db, 'activities');
    const q = query(
      eventsRef,
      where('createdBy', '==', userId)
    );
    
    const snapshot = await getDocs(q);
    const events: Event[] = [];
    
    snapshot.forEach((doc) => {
      events.push({
        id: doc.id,
        ...doc.data()
      } as Event);
    });
    
    // Ordenar en el cliente
    events.sort((a, b) => {
      const timeA = a.createdAt?.toMillis() || 0;
      const timeB = b.createdAt?.toMillis() || 0;
      return timeB - timeA;
    });
    
    return events;
  } catch (error) {
    console.error('❌ Error obteniendo eventos:', error);
    throw error;
  }
};

/**
 * Obtener un evento por ID
 */
export const getEventById = async (eventId: string): Promise<Event | null> => {
  try {
    const docRef = doc(db, 'activities', eventId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date()
      } as Event;
    }

    return null;
  } catch (error) {
    console.error('❌ Error getting event:', error);
    throw error;
  }
};

/**
 * Actualizar el status de un evento
 */
export const updateEventStatus = async (
  eventId: string, 
  status: number
): Promise<void> => {
  try {
    const eventRef = doc(db, 'activities', eventId);
    await updateDoc(eventRef, {
      status: status,
      published: status === EventStatus.PUBLISHED,
      updatedAt: serverTimestamp()
    });
    console.log('✅ Event status updated:', eventId, status);
  } catch (error) {
    console.error('❌ Error updating event status:', error);
    throw error;
  }
};

/**
 * Actualizar un evento existente
 */
export const updateEvent = async (
  eventId: string,
  data: UpdateEventData
): Promise<void> => {
  try {
    const eventRef = doc(db, 'activities', eventId);
    const updateData: Record<string, any> = {
      ...data,
      updatedAt: serverTimestamp()
    };

    if (data.date) {
      updateData.date = Timestamp.fromDate(data.date);
    }

    if (data.information) {
      updateData.searchKeywords = generateSearchKeywords(data.information);
    }

    if (data.lat !== undefined && data.long !== undefined) {
      updateData.geohash = generateSimpleGeohash(data.lat, data.long);
    }

    await updateDoc(eventRef, updateData);
    console.log('✅ Evento actualizado:', eventId);
  } catch (error) {
    console.error('❌ Error actualizando evento:', error);
    throw error;
  }
};

/**
 * Eliminar borrador de evento
 */
export const deleteDraftEvent = async (eventId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'activities', eventId));
    console.log('✅ Borrador eliminado:', eventId);
  } catch (error) {
    console.error('❌ Error eliminando borrador:', error);
    throw error;
  }
};

/**
 * Obtener etiqueta de estado
 */
export const getEventStatusLabel = (status: number, published: boolean): string => {
  if (!published && status === EventStatus.DRAFT) return 'status.draft';
  if (published && status === EventStatus.PUBLISHED) return 'status.published';
  if (status === EventStatus.PENDING_MODERATION) return 'status.pending_moderation';
  if (status === EventStatus.REJECTED) return 'status.rejected';
  if (status === EventStatus.EXPIRED) return 'status.expired';
  if (status === EventStatus.APPROVED) return 'status.approved';
  return 'status.unknown';
};

/**
 * Obtener color de estado
 */
export const getEventStatusColor = (status: number, published: boolean): string => {
  if (!published && status === EventStatus.DRAFT) return 'warning';
  if (published && status === EventStatus.PUBLISHED) return 'success';
  if (status === EventStatus.PENDING_MODERATION) return 'medium';
  if (status === EventStatus.REJECTED) return 'danger';
  if (status === EventStatus.EXPIRED) return 'medium';
  if (status === EventStatus.APPROVED) return 'tertiary';
  return 'medium';
};

/**
 * Generar geohash simple
 */
const generateSimpleGeohash = (lat: number, lng: number): string => {
  const latStr = lat.toFixed(4).replace('.', '').replace('-', 'n');
  const lngStr = lng.toFixed(4).replace('.', '').replace('-', 'w');
  return `${latStr.substring(0, 3)}${lngStr.substring(0, 2)}`;
};

/**
 * Generar keywords para búsqueda
 */
const generateSearchKeywords = (title: string): string[] => {
  const keywords: string[] = [];
  const cleanTitle = title.toLowerCase().trim();
  const words = cleanTitle.split(' ');
  
  words.forEach(word => {
    if (word.length >= 2) {
      for (let i = 2; i <= word.length; i++) {
        keywords.push(word.substring(0, i));
      }
    }
  });
  
  return Array.from(new Set(keywords));
};

/**
 * Crear nuevo evento (como borrador)
 */
export const createEvent = async (data: CreateEventData): Promise<string> => {
  try {
    const eventsRef = collection(db, 'activities');
    
    const eventData = {
      information: data.information,
      information2: data.information2,
      date: Timestamp.fromDate(data.date),
      schedule_start: data.schedule_start,
      schedule_finish: data.schedule_finish,
      price: data.price,
      address_id: data.address_id,
      lat: data.lat,
      long: data.long,
      geohash: generateSimpleGeohash(data.lat, data.long),
      photoItem: data.photoItem,
      photoPortrait: data.photoPortrait,
      videoUrl: data.videoUrl,
      artist_id: 'generic',
      searchKeywords: generateSearchKeywords(data.information),
      status: EventStatus.PENDING_MODERATION,
      published: false,
      expired: false,
      likes: [],
      createdBy: data.createdBy,
      createdByEmail: data.createdByEmail || '',
      paymentAmount: data.paymentAmount || 0.99,
      paymentCurrency: data.paymentCurrency || 'USD',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(eventsRef, eventData);
    console.log('✅ Evento creado pendiente de aprobación:', docRef.id);
    
    return docRef.id;
  } catch (error) {
    console.error('❌ Error creando evento:', error);
    throw error;
  }
};